<template>
  <div
    ref="container"
    v-click-outside="closeDropdown"
    v-tooltip.top="tooltip"
    :style="cssVars"
    :class="[
      'dropdown',
      { 'dropdown--full-width': useFullWidth },
      { 'dropdown--is-open': isOpen },
      { 'dropdown--is-disabled': isDisabled },
    ]"
    @keydown.esc="closeDropdown()"
    @keydown.arrow-up.prevent="updateActiveIndex('prev')"
    @keydown.arrow-down.prevent="updateActiveIndex()">
    <label v-if="labelText" class="dropdown__label">{{ labelText }}</label>

    <div :class="['dropdown__container', containerClasses, sizeClass]">
      <button
        v-show="!isOpen"
        ref="dropdownToggler"
        :aria-label="tStatic('toggleDropdown')"
        aria-haspopup="listbox"
        :aria-controls="`listbox-${id}`"
        :aria-expanded="isOpen"
        type="button"
        :class="['dropdown__toggler', sizeClass]"
        @click="openDropdown()">
        {{ dropdownText }}
        <oie-base-icon class="dropdown__toggler-icon" icon="chevronDown" size="s"></oie-base-icon>
      </button>
      <input
        ref="dropdownFilterInput"
        v-model.trim="filterParameters"
        v-bind="!isOpen ? {hidden: true} : {}"
        :placeholder="filterInputPlaceholder"
        type="search"
        :class="['dropdown__filter-input', {'dropdown__filter-input--visible': isOpen}]">
    </div>

    <ul
      v-show="isOpen"
      :id="`listbox-${id}`"
      class="dropdown__options"
      role="listbox">
      <li v-if="enableAddOption" class="dropdown__option dropdown__option--no-hover">
        <oie-base-action theme="outlined" @click="addOption">
          {{ tStatic('add') }}
          <template #iconAppend>
            <oie-base-icon icon="add" size="s"></oie-base-icon>
          </template>
        </oie-base-action>
      </li>
      <li
        v-for="(option, i) of dropdownOptions"
        :id="`option-${i}-${id}`"
        :key="option.value"
        role="option"
        tabindex="0"
        :class="['dropdown__option', {'dropdown__option--deselectable': enableDeselection, 'dropdown__option--selected': option.value === value}]"
        :aria-selected="option.value === value ? 'true' : 'false'"
        :data-pw="option.value"
        @click="selectOption(option)"
        @keydown.enter.prevent="selectOption(option)">
        <span class="dropdown__option-text">
          {{ option.text }}
          <span v-if="option.value === value && enableDeselection" class="sr-only">{{ tStatic('pressToDeselectAria') }}</span>
          <oie-base-icon v-if="option.value === value" icon="checkmark" size="xs"></oie-base-icon>
        </span>
        <oie-base-icon
          v-if="option.value === value && enableDeselection"
          class="dropdown__option--delete"
          icon="trash-can"
          size="xs"></oie-base-icon>
      </li>
    </ul>
    <div v-if="errorMessages.length > 0 || informationMessage" class="dropdown__message">
      <template v-for="error in errorMessages">
        <div :key="`dropdown-error-${error}`" class="dropdown__message-error">
          {{ error }}
        </div>
      </template>
      <div v-html="informationMessage"></div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    initialText: {
      type: String,
      default: ''
    },
    defaultSelected: {
      type: [String, Object],
      default: null
    },
    labelText: {
      type: String,
      default: ''
    },
    containerClasses: {
      type: String,
      default: ''
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    informationMessage: {
      type: String,
      default: ''
    },
    isLarge: {
      type: Boolean,
      default: true
    },
    options: {
      type: Array,
      default: () => []
    },
    name: {
      type: String,
      default: ''
    },
    useFullWidth: {
      type: Boolean,
      default: false
    },
    useWildCardSearch: {
      type: Boolean,
      default: false
    },
    filterInputPlaceholder: {
      type: String,
      default: null
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    zIndex: {
      type: String,
      default: '3'
    },
    enableAddOption: {
      type: Boolean,
      default: false
    },
    enableDeselection: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    isOpen: false,
    filterParameters: '',
    id: '',
    activeIndex: -1
  }),
  computed: {
    dropdownText() {
      if (!this.value) {
        return this.initialText
      } else {
        const selection = _.find(this.options, o => o.value === this.value)
        return selection ? selection.text : this.value
      }
    },
    sizeClass() {
      return `dropdown__container--${this.isLarge ? 'large' : 'medium'}`
    },
    dropdownOptions() {
      if (this.filterParameters.length > 0) {
        if (this.useWildCardSearch) {
          return this.options.filter(c => { return c.text.toLowerCase().includes(this.filterParameters.toLowerCase()) })
        } else {
          return this.options.filter(c => { return c.text.toLowerCase().startsWith(this.filterParameters.toLowerCase()) })
        }
      } else {
        return this.options
      }
      // return this.filterParameters.length > 0 ? this.options.filter(c => { return c.text.toLowerCase().startsWith(this.filterParameters.toLowerCase()) }) : this.options
    },
    cssVars() {
      return {
        '--z-index': this.zIndex
      }
    }
  },
  mounted() {
    this.name ? this.id = this.name : this.id = _.uniqueId('base-dropdown-')

    if(this.options != null) {
      if(this.defaultSelected) {
        const defaultOption = _.find(this.options, o => o.value === this.defaultSelected)
        if(defaultOption) {
          this.selectOption(defaultOption)
        }
      } else if (this.initialText === null || this.initialText === '') {
        this.selectOption(this.options[0])
      }
    }

    this.subscribe('dropdown.close', (name) => {
      if (this.name === name) {
        this.closeDropdown()
      }
    })

    document.addEventListener('focusin', this.checkFocus)
  },
  beforeDestroy() {
    document.removeEventListener('focusin', this.checkFocus)
  },
  methods: {
    updateActiveIndex(direction = 'next') {
      if (!this.isOpen) return
      if (this.dropdownOptions.length === 0) return

      const newIndex = direction === 'next' ? this.activeIndex + 1 : this.activeIndex - 1
      const validIndex = this.dropdownOptions[newIndex]
      if (validIndex) {
        this.activeIndex = newIndex
      } else {
        this.activeIndex = (direction === 'next' ? 0 : this.dropdownOptions.length - 1)
      }
      try {
        document.getElementById(`option-${this.activeIndex}-${this.id}`).focus()
      } catch {
        console.warn('Could not focus element')
      }
    },
    checkFocus() {
      const container = this.$refs.container
      const active = document.activeElement
      if (!container.contains(active)) {
        this.closeDropdown(false)
      }
    },
    async openDropdown() {
      this.isOpen = true
      await this.$nextTick()
      this.$refs.dropdownFilterInput.focus()
    },
    async closeDropdown(focusToggler = true) {
      if (!this.isOpen) return
      this.isOpen = false
      if(focusToggler) {
        await this.$nextTick()
        this.$refs.dropdownToggler.focus()
      }
    },
    async selectOption(option) {
      if (!option) {
        return
      }
      this.isOpen = false
      this.filterParameters = ''

      const alreadySelected = this.value === option.value
      if(alreadySelected && this.enableDeselection) {
        if(this.name !== '') {
          this.publish(`dropdown-option-selected-${this.name}`, null)
        }

        this.$emit('input', null)
        this.$emit('selection', null)
      } else {
        if(this.name !== '') {
          this.publish(`dropdown-option-selected-${this.name}`, option)
        }

        this.$emit('input', option.value)
        this.$emit('selection', option)
      }

      await this.$nextTick()
      this.$refs.dropdownToggler.focus()
    },
    resetDropdown() {
      this.selectOption(this.initialText !== null
        ? {
          text: this.initialText,
          value: null
        } : this.options[0]
      )
    },
    addOption() {
      const currentValue = this.filterParameters
      this.$emit('add-option', currentValue)
      this.filterParameters = ''
      this.closeDropdown()
    }
  }
}
</script>

<style lang="scss">
.dropdown {
  position: relative;
  max-width: 15.125rem;
}

.dropdown--full-width {
  width: 100%;
  max-width: initial;
}

.dropdown--is-disabled {
  pointer-events: none;
  opacity: .7;
}

.dropdown__label {
  @include body-smaller-semi-bold;
  display: block;
  margin-bottom: measure(eighth);
}

.dropdown__container {
  position: relative;
  display: flex;
  width: 100%;
  border: 1px solid color(border-gray);
  background-color: color(background-white);
  z-index: 3;
}

.dropdown__container--medium {
  height: calc(2.75rem - 0.25rem);
}

.dropdown__container--large {
  height: calc(3.125rem - 0.25rem);
}

.dropdown--is-open .dropdown__container {
  border-bottom-color: transparent;
}

.dropdown__toggler {
  @include reset-button;
  position: relative;
  background-color: transparent;
  padding-left: 1.25rem;
  padding-right: 2.5em;
  width: 100%;
  height: 100%;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.dropdown__toggler-icon {
  position: absolute;
  right: 10px;
}

.dropdown__toggler:hover {
  cursor: pointer;
}

.dropdown--is-open .dropdown__toggler:after {
  transform: translateY(-50%) scale(-1);
}

.dropdown__options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: inline-block;
  list-style-type: none;
  margin: 0;
  margin-top: -2px;
  width: 100%;
  max-width: 100%;
  max-height: 19rem;
  text-align: left;
  background-color: color(background-white);
  border: 2px solid color(border-gray);
  z-index: var(--z-index);
  overflow: auto;
}

.dropdown--thin-border .dropdown__options {
  margin-top: -1px;
  border-width: 1px;
}

.dropdown--right .dropdown__options {
  left: initial;
  white-space: nowrap;
}

.dropdown--left .dropdown__options {
  right: initial;
  white-space: nowrap;
}

.dropdown__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1.25rem;

  &:hover,
  &:focus {
    background-color: color(blue-4);
    cursor: pointer;
  }

}

.dropdown__option-text {
  display: flex;
  align-items: center;
  gap: measure(eighth);
}

.dropdown__option--deselectable:hover .dropdown__option--delete {
  color: color(red-4);
}

.dropdown__option--selected {
  @include body-semi-bold;
  background-color: color(blue-4);
}

.dropdown__option--no-hover:hover {
  background-color: initial;
  cursor: initial;
}

.dropdown__filter-input {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  opacity: 0;
}

.dropdown__filter-input--visible {
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 1;
  padding: measure(eighth);
}

.dropdown__message {
  @include body-smaller;
  padding: measure(quarter) 0;
}

.dropdown__message-error {
  color: color(red-1);
}
</style>
